// src/components/DepartmentOverview.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import neo4jService from '../services/neo4jService';
import { departmentQueries } from '../repository/graphQueries';

const DepartmentOverview = () => {
    const { dateRange, selectedDepartments: selectedDepartmentsFromContext, allDepartmentIds, neo4jService: svcFromContext } = useOutletContext() || {};
    const [departments, setDepartments] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [goals, setGoals] = useState([]);
    const [initiatives, setInitiatives] = useState([]);
    const [issues, setIssues] = useState([]);
    const [actionItems, setActionItems] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [tabCommunications, setTabCommunications] = useState({ goals: [], initiatives: [], issues: [], actions: [], overview: [] });
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = useState([]);
    const [showAll, setShowAll] = useState({ goals: false, initiatives: false, issues: false, actions: false });
    const [driDropdownOpen, setDriDropdownOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview'); // overview | goals | initiatives | issues | actions | communications

    // Active Neo4j service from context or fallback
    const activeNeo4jService = svcFromContext || neo4jService;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (!activeNeo4jService.driver) {
                const uri = process.env.REACT_APP_NEO4J_URI || 'neo4j://localhost:7687';
                const user = process.env.REACT_APP_NEO4J_USER || 'neo4j';
                const pass = process.env.REACT_APP_NEO4J_PASSWORD || 'password';
                await activeNeo4jService.initialize(uri, user, pass);
            }

            const deptResult = await activeNeo4jService.runQuery(departmentQueries.listAllDepartments);
            const deptList = deptResult.map(r => r.department);
            setDepartments(deptList);
            setSelectedDepartments(selectedDepartmentsFromContext && selectedDepartmentsFromContext.length ? selectedDepartmentsFromContext : deptList);
            // Load DRIs for initial selection
            const driRes = await activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentDRIs, { departments: selectedDepartmentsFromContext && selectedDepartmentsFromContext.length ? selectedDepartmentsFromContext : deptList });
            setDris(driRes.map(r => r.dri));
        } catch (err) {
            console.error("Error fetching overview data:", err);
            setError("Failed to load data. " + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeNeo4jService]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchLists = useCallback(async () => {
        if (!selectedDepartments || selectedDepartments.length === 0 || !dateRange) return;
        setLoading(true);
        try {
            const params = { departments: selectedDepartments, startDate: dateRange.start, endDate: dateRange.end, driFilter: selectedDris.length ? selectedDris : null };
            const [g, i, iss, ai, comms] = await Promise.all([
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentGoalsList, params),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentInitiativesList, params),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentIssuesList, params),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentActionItemsList, params),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentCommunicationsList, params),
            ]);
            setGoals(g);
            setInitiatives(i);
            setIssues(iss);
            setActionItems(ai);
            setCommunications(comms);

            // Fetch per-tab communications linked to currently listed items
            const entityIds = {
                goals: (g || []).map(x => x.id),
                initiatives: (i || []).map(x => x.id),
                issues: (iss || []).map(x => x.id),
                actions: (ai || []).map(x => x.id),
            };
            const commParamsBase = { departments: selectedDepartments, startDate: dateRange.start, endDate: dateRange.end, driFilter: selectedDris.length ? selectedDris : null };
            const [goalsComms, initiativesComms, issuesComms, actionsComms] = await Promise.all([
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentCommunicationsForEntities, { ...commParamsBase, entityIds: entityIds.goals }),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentCommunicationsForEntities, { ...commParamsBase, entityIds: entityIds.initiatives }),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentCommunicationsForEntities, { ...commParamsBase, entityIds: entityIds.issues }),
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentCommunicationsForEntities, { ...commParamsBase, entityIds: entityIds.actions }),
            ]);
            setTabCommunications({ goals: goalsComms, initiatives: initiativesComms, issues: issuesComms, actions: actionsComms, overview: comms });
        } catch (err) {
            console.error('Error loading department lists:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [activeNeo4jService, selectedDepartments, dateRange, selectedDris]);

    useEffect(() => { fetchLists(); }, [fetchLists]);

    // Sync when selection from context changes
    useEffect(() => {
        if (selectedDepartmentsFromContext && selectedDepartmentsFromContext.length) {
            setSelectedDepartments(selectedDepartmentsFromContext);
            // Refresh DRIs when department selection changes
            if (activeNeo4jService?.driver) {
                activeNeo4jService.runQuery(departmentQueries.getMultiDepartmentDRIs, { departments: selectedDepartmentsFromContext })
                    .then(res => setDris(res.map(r => r.dri)))
                    .catch(() => setDris([]));
            }
        }
    }, [selectedDepartmentsFromContext]);

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-md">
            <strong>Error:</strong> {error} 
            <button onClick={fetchData} className="ml-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm">Retry</button>
        </div>;
    }

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Loading Department View...</span></div>;
    }

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-md"><strong>Error:</strong> {error}</div>;
    }

    const renderTable = (title, rows, columns, entityBasePath, limitKey) => (
        <div className="bg-white rounded-lg shadow p-3">
            <h3 className="text-lg font-medium text-gray-900 mb-3">{title}</h3>
            <div className="overflow-x-auto">
                <table className="min-w-full table-fixed text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} className={`px-3 py-2 text-left font-medium text-gray-600 ${col.width || 'w-auto'}`}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {(showAll[limitKey] ? rows : rows.slice(0, 5)).map(row => (
                            <tr key={row.id} className="hover:bg-gray-50">
                                {columns.map(col => (
                                    <td key={col.key} className={`px-3 py-2 align-top ${col.tdClassName || ''}`}>
                                        {col.key === 'name' ? (
                                            <Link className="text-blue-700 hover:text-blue-900" to={`/internal/${entityBasePath}/${encodeURIComponent(row.id)}`}>{row[col.key]}</Link>
                                        ) : col.render ? (
                                            col.render(row)
                                        ) : (
                                            typeof row[col.key] === 'number' ? row[col.key] : (Array.isArray(row[col.key]) ? row[col.key].length : (row[col.key] ?? '-'))
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={columns.length}>No items found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {rows.length > 5 && (
                <div className="mt-2 text-right">
                    <button className="text-xs text-blue-700 hover:text-blue-900" onClick={() => setShowAll(prev => ({ ...prev, [limitKey]: !prev[limitKey] }))}>
                        {showAll[limitKey] ? 'see less' : 'see more'}
                    </button>
                </div>
            )}
        </div>
    );

    const goalColumnsCompact = [
        { key: 'name', label: 'Goal' },
        { key: 'initiativeCount', label: 'Initiatives' },
        { key: 'blockingIssuesCount', label: 'Issues' },
        { key: 'score', label: 'Importance' },
    ];
    const initiativeColumnsCompact = [
        { key: 'name', label: 'Initiative' },
        { key: 'supportedGoals', label: 'Goals' },
        { key: 'blockingIssuesCount', label: 'Issues' },
        { key: 'actionItemCount', label: 'Actions' },
        { key: 'score', label: 'Importance' },
    ];
    const issueColumnsCompact = [
        { key: 'name', label: 'Issue' },
        { key: 'severity', label: 'Severity' },
        { key: 'blockedInitiatives', label: 'Initiatives' },
        { key: 'score', label: 'Importance' },
    ];
    const actionItemColumnsCompact = [
        { key: 'name', label: 'Action Item' },
        { key: 'assigned_to', label: 'Assignee' },
        { key: 'initiativeNames', label: 'Initiatives' },
        { key: 'deadline', label: 'Deadline' },
        { key: 'score', label: 'Importance' },
    ];

    const goalColumnsFull = [
        { key: 'name', label: 'Goal', width: 'w-1/4' },
        { key: 'assigned_to', label: 'DRI' },
        { key: 'status', label: 'Status' },
        { key: 'initiativeCount', label: 'Initiatives' },
        { key: 'blockingIssuesCount', label: 'Blockers' },
        { key: 'score', label: 'Importance' },
        { key: 'lastCommunicationDate', label: 'Last Mentioned' },
        { key: 'description', label: 'Description', width: 'w-1/3', tdClassName: 'whitespace-normal break-words', render: (row) => (<TruncatedText text={row.description} />) },
    ];

    const initiativeColumnsFull = [
        { key: 'name', label: 'Initiative', width: 'w-1/4' },
        { key: 'assigned_to', label: 'DRI' },
        { key: 'status', label: 'Status' },
        { key: 'supportedGoals', label: 'Supported Goals', width: 'w-1/4', tdClassName: 'whitespace-normal break-words', render: (row) => (Array.isArray(row.supportedGoals) ? row.supportedGoals.join(', ') : '-') },
        { key: 'blockingIssuesCount', label: 'Blockers' },
        { key: 'actionItemCount', label: 'Action Items' },
        { key: 'score', label: 'Importance' },
        { key: 'description', label: 'Description', width: 'w-1/3', tdClassName: 'whitespace-normal break-words', render: (row) => (<TruncatedText text={row.description} />) },
    ];

    const issueColumnsFull = [
        { key: 'name', label: 'Issue', width: 'w-1/4' },
        { key: 'assigned_to', label: 'DRI' },
        { key: 'severity', label: 'Severity' },
        { key: 'status', label: 'Status' },
        { key: 'blockedInitiatives', label: 'Blocked Initiatives', width: 'w-1/4', tdClassName: 'whitespace-normal break-words', render: (row) => (Array.isArray(row.blockedInitiatives) ? row.blockedInitiatives.join(', ') : '-') },
        { key: 'score', label: 'Importance' },
        { key: 'description', label: 'Description', width: 'w-1/3', tdClassName: 'whitespace-normal break-words', render: (row) => (<TruncatedText text={row.description} />) },
    ];

    const actionItemColumnsFull = [
        { key: 'name', label: 'Action Item', width: 'w-1/4' },
        { key: 'assigned_to', label: 'Assignee' },
        { key: 'status', label: 'Status' },
        { key: 'deadline', label: 'Deadline' },
        { key: 'initiativeNames', label: 'Initiatives', width: 'w-1/4', tdClassName: 'whitespace-normal break-words', render: (row) => (Array.isArray(row.initiativeNames) ? row.initiativeNames.join(', ') : '-') },
        { key: 'score', label: 'Importance' },
        { key: 'description', label: 'Description', width: 'w-1/3', tdClassName: 'whitespace-normal break-words', render: (row) => (<TruncatedText text={row.description} />) },
    ];


    return (
        <div className="container mx-auto p-4 space-y-6">
            {/* Header stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard title="High Sev. Issues" value={(issues || []).filter(i => i.severity === 'high').length} accent="red" />
                <StatCard title="Blocked Initiatives" value={(initiatives || []).filter(i => (i.blockingIssuesCount || 0) > 0).length} accent="orange" />
                <StatCard title="Overdue Actions" value={(actionItems || []).filter(a => a.deadline && new Date(a.deadline) < new Date()).length} accent="yellow" />
                <StatCard title="Active Goals" value={(goals || []).length} accent="purple" />
            </div>
            {/* DRI filter dropdown */}
            <div className="flex items-center gap-3">
                <div className="text-sm text-gray-700">Filter by DRI:</div>
                <div className="relative">
                    <button
                        type="button"
                        className="inline-flex items-center px-2 py-1 md:px-3 md:py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                        onClick={() => setDriDropdownOpen(o => !o)}
                    >
                        {selectedDris.length === 0 ? 'All DRIs' : `${selectedDris.length} selected`}
                        <svg className="ml-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
                    </button>
                    {driDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                            <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                                <label className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="form-checkbox"
                                        checked={selectedDris.length === 0}
                                        onChange={() => setSelectedDris([])}
                                    />
                                    <span className="text-sm">All DRIs</span>
                                </label>
                                {dris.map((dri) => (
                                    <label key={dri} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox"
                                            checked={selectedDris.includes(dri)}
                                            onChange={() => setSelectedDris(prev => prev.includes(dri) ? prev.filter(x => x !== dri) : [...prev, dri])}
                                        />
                                        <span className="text-sm">{dri}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 border-b">
                {['overview','goals','initiatives','issues','actions','communications'].map(tab => (
                    <button
                        key={tab}
                        className={`px-3 py-2 text-sm -mb-px border-b-2 ${activeTab === tab ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab === 'overview' ? 'Overview' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {activeTab === 'overview' ? (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {renderTable('Top Priority Goals', goals, goalColumnsCompact, 'goals', 'goals')}
                        {renderTable('Key Initiatives', initiatives, initiativeColumnsCompact, 'initiatives', 'initiatives')}
                        {renderTable('Critical Issues', issues, issueColumnsCompact, 'issues', 'issues')}
                        {renderTable('Pending Action Items', actionItems, actionItemColumnsCompact, 'action-items', 'actions')}
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 mt-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Recent Communications</h3>
                        <div className="space-y-3">
                            {(tabCommunications.overview?.length ? tabCommunications.overview : communications).map(comm => (
                                <CommunicationCard key={comm.id} comm={comm} />
                            ))}
                            {((tabCommunications.overview?.length ? tabCommunications.overview : communications).length === 0) && (
                                <div className="text-sm text-gray-500">No communications in this range.</div>
                            )}
                        </div>
                    </div>
                </>
            ) : activeTab === 'goals' ? (
                <div className="grid grid-cols-1 gap-4">
                    {renderTable('Goals', goals, goalColumnsFull, 'goals', 'goalsFull')}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Relevant Communications</h3>
                        <div className="space-y-3">
                            {(tabCommunications.goals || []).map(comm => (
                                <CommunicationCard key={comm.id} comm={comm} />
                            ))}
                            {(tabCommunications.goals || []).length === 0 && (
                                <div className="text-sm text-gray-500">No communications in this range.</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : activeTab === 'initiatives' ? (
                <div className="grid grid-cols-1 gap-4">
                    {renderTable('Initiatives', initiatives, initiativeColumnsFull, 'initiatives', 'initiativesFull')}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Relevant Communications</h3>
                        <div className="space-y-3">
                            {(tabCommunications.initiatives || []).map(comm => (
                                <CommunicationCard key={comm.id} comm={comm} />
                            ))}
                            {(tabCommunications.initiatives || []).length === 0 && (
                                <div className="text-sm text-gray-500">No communications in this range.</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : activeTab === 'issues' ? (
                <div className="grid grid-cols-1 gap-4">
                    {renderTable('Issues', issues, issueColumnsFull, 'issues', 'issuesFull')}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Relevant Communications</h3>
                        <div className="space-y-3">
                            {(tabCommunications.issues || []).map(comm => (
                                <CommunicationCard key={comm.id} comm={comm} />
                            ))}
                            {(tabCommunications.issues || []).length === 0 && (
                                <div className="text-sm text-gray-500">No communications in this range.</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : activeTab === 'actions' ? (
                <div className="grid grid-cols-1 gap-4">
                    {renderTable('Action Items', actionItems, actionItemColumnsFull, 'action-items', 'actionsFull')}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Relevant Communications</h3>
                        <div className="space-y-3">
                            {(tabCommunications.actions || []).map(comm => (
                                <CommunicationCard key={comm.id} comm={comm} />
                            ))}
                            {(tabCommunications.actions || []).length === 0 && (
                                <div className="text-sm text-gray-500">No communications in this range.</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Communications</h3>
                    <div className="space-y-3">
                        {communications.map(comm => (
                            <CommunicationCard key={comm.id} comm={comm} />
                        ))}
                        {communications.length === 0 && (
                            <div className="text-sm text-gray-500">No communications in this range.</div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

const StatCard = ({ title, value, accent }) => {
    const colorMap = {
        red: ['bg-red-100', 'text-red-700', 'text-red-800'],
        orange: ['bg-orange-100', 'text-orange-700', 'text-orange-800'],
        yellow: ['bg-yellow-100', 'text-yellow-700', 'text-yellow-800'],
        purple: ['bg-purple-100', 'text-purple-700', 'text-purple-800'],
    };
    const [bg, label, valueColor] = colorMap[accent] || ['bg-gray-100', 'text-gray-600', 'text-gray-900'];
    return (
        <div className={`${bg} border rounded-lg p-3`}>
            <div className={`text-xs ${label} mb-1`}>{title}</div>
            <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
        </div>
    );
};

    const CommunicationCard = ({ comm }) => {
    const [expanded, setExpanded] = useState(false);
    const snippet = comm.summary || '';
    const showToggle = snippet && snippet.length > 180;
    const display = expanded ? snippet : snippet?.slice(0, 180);
    return (
        <div className="border rounded-md p-3">
            <div className="text-xs text-gray-500 mb-1">{new Date(comm.date).toLocaleString()}</div>
            <div className="font-medium mb-1">{comm.title || 'Untitled'}</div>
            <div className="text-sm text-gray-700">
                {display}
                {showToggle && (
                    <button className="ml-2 text-blue-700 hover:text-blue-900 text-xs" onClick={() => setExpanded(e => !e)}>{expanded ? 'see less' : 'see more'}</button>
                )}
            </div>
            {Array.isArray(comm.evidencedEntities) && comm.evidencedEntities.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {comm.evidencedEntities.map((e, idx) => (
                        <span key={`${e.type}-${e.name}-${idx}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                            {e.type}: {e.name}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// Compact multiline text with a show more/less toggle used in table cells
const TruncatedText = ({ text, maxChars = 120 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text) return <span>-</span>;
    const str = String(text);
    const isLong = str.length > maxChars;
    const display = expanded || !isLong ? str : str.slice(0, maxChars);
    return (
        <span className="text-gray-700">
            {display}
            {isLong && (
                <button
                    className="ml-1 text-blue-700 hover:text-blue-900 text-xs whitespace-nowrap align-baseline"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(v => v ? false : true); }}
                >
                    {expanded ? 'see less' : 'see more'}
                </button>
            )}
        </span>
    );
};

export default DepartmentOverview; 